<template>
  <BaseWidget
    :title="widgetTitle"
    :show-info-icon="true"
    info-tooltip="Overview of invoices by status, based on each invoice's due date within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/4753023-quick-guide-to-invoices', text: 'Learn about Invoices' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :date-range="dateRange"
    :overflow-menu-items="taxMenuItems"
    @update:date-range="handleDateRangeChange"
    @overflow-select="handleOverflowSelect"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="widget-content-wrapper"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <div class="chart-container stacked">
        <div
          v-if="invoiceStatuses.length === 0"
          class="no-data"
        >
          No outstanding invoices
        </div>
        <template v-else>
          <!-- Single stacked horizontal bar -->
          <div class="stacked-bar-wrapper">
            <div class="stacked-bar">
              <div
                v-for="(item, index) in invoiceStatuses"
                :key="item.name"
                class="stacked-bar-segment"
                :style="{
                  width: `${getStackedSegmentWidth(item.amount)}%`,
                  backgroundColor: getBarColor(item.name)
                }"
                @mouseenter="(e) => showSegmentTooltip(e, item)"
                @mouseleave="hideSegmentTooltip"
                @click="navigateToStatus(item.name)"
              />
            </div>
          </div>
          <div class="date-range-row">
            <div
              v-if="subtitleText"
              class="date-range-label"
            >
              {{ subtitleText }}
            </div>
            <a
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
          <!-- Legend table: color square | status name | amount -->
          <div
            v-if="!focused"
            class="legend-table"
          >
            <div
              v-for="(item, index) in invoiceStatuses"
              :key="item.name"
              class="legend-row"
              @click="navigateToStatus(item.name)"
            >
              <div class="legend-label">
                <span
                  class="legend-color-swatch"
                  :style="{ backgroundColor: getBarColor(item.name) }"
                />
                <span class="legend-title">{{ item.name }}</span>
              </div>
              <div class="legend-count">
                {{ formatAmount(item.amount) }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="segmentTooltip.show && segmentTooltip.position"
        ref="segmentTooltipRef"
        class="stacked-bar-tooltip"
        :style="segmentTooltipStyle"
      >
        {{ segmentTooltip.text }}
      </div>
    </Teleport>
  </BaseWidget>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import BaseWidget from './BaseWidget.vue'

import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useInvoicesByStatusData } from '../composables/useWidgetData.js'
import { DATE_RANGE_LABELS, getDateRangeTimestamps, getDateRangeDates } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_30_days')
const excludeTax = ref(props.props?.excludeTax || false)

const widgetTitle = 'Invoices'

const taxMenuItems = computed(() => [
  { label: 'Exclude tax', value: 'exclude-tax', active: excludeTax.value },
  { label: 'Include tax', value: 'include-tax', active: !excludeTax.value }
])

const dateRangeLabel = computed(() => DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days')

// Keep the excl-tax state in the subtitle so the title can't wrap and hide labels (SDWEB-18441).
const subtitleText = computed(() => {
  return excludeTax.value ? `${dateRangeLabel.value} · Excl. tax` : dateRangeLabel.value
})

const { data, loading, error, cleanup, refresh } = useInvoicesByStatusData({
  priority: 1,
  variables: computed(() => ({ dateRange: dateRange.value, excludeTax: excludeTax.value }))
})

const invoiceStatuses = computed(() => {
  return data.value?.invoicesByStatus?.statuses || []
})

const totalAmount = computed(() => {
  return invoiceStatuses.value.reduce((sum, s) => sum + s.amount, 0) || 1
})

const getStackedSegmentWidth = (amount) => {
  if (totalAmount.value === 0) return 0
  return (amount / totalAmount.value) * 100
}

const STATUS_COLORS = {
  Upcoming: '#4a86b1',
  Due: '#c79a2f',
  Overdue: '#d64b4b',
  Processing: '#966ca6',
  Paid: '#4a907a',
  Void: '#7d7d7d'
}
const getBarColor = (statusName) => STATUS_COLORS[statusName] || '#7d7d7d'

const normalizeLocale = (locale) => {
  if (!locale) return 'en-US'
  return locale.replace(/_/g, '-')
}

const formatAmount = (amount) => {
  const info = data.value?.invoicesByStatus
  const currencySymbol = info?.currency_symbol || '$'
  const rawLocale = info?.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = info?.currency || 'USD'

  if (amount === 0) {
    return `${currencySymbol}0`
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (e) {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const segmentTooltipRef = ref(null)
const segmentTooltip = ref({ show: false, text: '', position: null })

const segmentTooltipStyle = computed(() => {
  if (!segmentTooltip.value.position) return {}
  const { left, top } = segmentTooltip.value.position
  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateX(-50%)'
  }
})

const showSegmentTooltip = (e, item) => {
  const el = e.currentTarget
  const rect = el.getBoundingClientRect()
  const tooltipHeight = 36
  const margin = 6
  segmentTooltip.value = {
    show: true,
    text: `${formatAmount(item.amount)} in ${item.name.toLowerCase()} invoices`,
    position: {
      left: rect.left + rect.width / 2,
      top: rect.top - tooltipHeight - margin
    }
  }
  nextTick(() => {
    if (segmentTooltipRef.value) {
      const h = segmentTooltipRef.value.offsetHeight
      segmentTooltip.value = {
        ...segmentTooltip.value,
        position: {
          left: rect.left + rect.width / 2,
          top: rect.top - h - margin
        }
      }
    }
  })
}

const hideSegmentTooltip = () => {
  segmentTooltip.value = { show: false, text: '', position: null }
}

const handleTooltipScroll = () => {
  if (segmentTooltip.value.show) hideSegmentTooltip()
}

const handleTooltipResize = () => {
  if (segmentTooltip.value.show) hideSegmentTooltip()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  }
}

// Navigate to /invoices filtered by a specific status
const navigateToStatus = (statusName) => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const dates = getDateRangeDates(dateRange.value)
  const status = statusName.toUpperCase()
  const navigationUrl = `/invoices?status=${status}&due_date_from=${dates.from}&due_date_to=${dates.to}`

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (e) {
    console.error('Error posting ReplaceUrl message:', e)
  }
}

// Handle "View all" click - navigate to /invoices via parent window
const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const dates = getDateRangeDates(dateRange.value)
  const navigationUrl = `/invoices?due_date_from=${dates.from}&due_date_to=${dates.to}`

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (e) {
    console.error('Error posting ReplaceUrl message:', e)
  }
}

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange, excludeTax: excludeTax.value })
  }
}

const handleOverflowSelect = (value) => {
  const newExcludeTax = value === 'exclude-tax'
  if (newExcludeTax !== excludeTax.value) {
    excludeTax.value = newExcludeTax
    emit('update:props', { dateRange: dateRange.value, excludeTax: newExcludeTax })
  }
}

watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

watch(() => props.props?.excludeTax, (newVal) => {
  if (newVal !== undefined && newVal !== excludeTax.value) {
    excludeTax.value = newVal
  }
})

watch(dateRange, () => {
  refresh(true, { showLoading: true })
})

watch(excludeTax, () => {
  refresh(true, { showLoading: true })
})

onMounted(() => {
  window.addEventListener('scroll', handleTooltipScroll, true)
  window.addEventListener('resize', handleTooltipResize)
})

onUnmounted(() => {
  cleanup()
  window.removeEventListener('scroll', handleTooltipScroll, true)
  window.removeEventListener('resize', handleTooltipResize)
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* Stacked bar + legend */
.chart-container.stacked {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  padding: 8px 0 0 0;
}

.date-range-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.date-range-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  flex-shrink: 0;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
}

.stacked-bar-wrapper {
  width: 100%;
  flex-shrink: 0;
  padding-bottom: 2px;
}

.stacked-bar {
  display: flex;
  gap: 1px;
  width: 100%;
  height: 16px;
  border-radius: 4px;
  overflow: visible;
  background-color: #f3f4f6;
}

.stacked-bar-segment {
  height: 100%;
  min-width: 2px;
  transition: opacity 0.2s ease;
  position: relative;
  cursor: pointer;
}

.stacked-bar-segment:first-child {
  border-radius: 4px 0 0 4px;
}

.stacked-bar-segment:last-child {
  border-radius: 0 4px 4px 0;
}

.stacked-bar-segment:hover {
  opacity: 0.9;
}

/* Teleported tooltip - renders in body to escape stacking context */
.stacked-bar-tooltip {
  position: fixed;
  padding: 6px 10px;
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10010;
}

/* Legend table */
.legend-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 48px;
  flex-shrink: 0;
  padding: 12px 4px 12px 0;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.legend-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.legend-row:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.legend-row:last-child {
  border-bottom: none;
}

.legend-label {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.legend-color-swatch {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.legend-title {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-count {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  flex-shrink: 0;
}

</style>
